/**
 * BulletRenderer — bar + markLine (target) + markArea (poor/ok/good bands).
 *
 * The canonical "are we on track?" viz. Each bar is one entity (or a single
 * "current" bar for KPI tiles); the target line and qualitative bands give
 * context at a glance. Used by:
 *   - Goals page bullets (one bullet per goal)
 *   - KPI status row tiles (current value vs prior-FY, with target overlay)
 *   - Per-widget goal overlay variant (when a measure has a goal)
 *
 * Phase C1 of issue #90 v3 plan.
 */
import ReactECharts from 'echarts-for-react'
import type { PerfRow, MetricKey, GroupByDimension } from './types'
import { METRIC_LABELS, formatMetric, getDimensionLabel } from './types'
import { useExecChartTheme } from './echarts-theme'

interface Props {
  data: PerfRow[]
  groupBy: GroupByDimension
  metrics: MetricKey[]
  /** Target value to display as a horizontal markLine. */
  target?: number
  /**
   * Performance bands as ordered fractions of the target (e.g., [0.7, 0.9])
   * draw as background markAreas: poor (0–t1), ok (t1–t2), good (t2–max).
   * If omitted, no bands are drawn.
   */
  bands?: [number, number]
  /** Higher-is-better (default) or lower-is-better target direction. */
  direction?: 'higher_is_better' | 'lower_is_better'
  /** Optional axis label override for the target line. */
  targetLabel?: string
}

const POOR_COLOR = 'rgba(239, 68, 68, 0.10)'
const OK_COLOR = 'rgba(245, 158, 11, 0.10)'
const GOOD_COLOR = 'rgba(34, 197, 94, 0.10)'

export default function BulletRenderer({
  data, groupBy, metrics, target, bands, direction = 'higher_is_better', targetLabel,
}: Props) {
  const theme = useExecChartTheme()
  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        No data
      </div>
    )
  }

  const metric = metrics[0]
  const categories = data.map((r) => getDimensionLabel(r, groupBy))
  const values = data.map((r) => {
    const v = r[metric]
    return typeof v === 'number' ? v : parseFloat(String(v)) || 0
  })

  // Qualitative bands. For higher-is-better: poor ≤ band[0], ok ∈ (band[0], band[1]],
  // good > band[1]. For lower-is-better: invert (lower is good).
  const markAreas: any[] = []
  if (target !== undefined && bands && bands.length === 2) {
    const t1 = target * bands[0]
    const t2 = target * bands[1]
    const max = Math.max(...values, target) * 1.1
    if (direction === 'lower_is_better') {
      markAreas.push(
        [{ yAxis: 0, itemStyle: { color: GOOD_COLOR } }, { yAxis: t1 }],
        [{ yAxis: t1, itemStyle: { color: OK_COLOR } }, { yAxis: t2 }],
        [{ yAxis: t2, itemStyle: { color: POOR_COLOR } }, { yAxis: max }],
      )
    } else {
      markAreas.push(
        [{ yAxis: 0, itemStyle: { color: POOR_COLOR } }, { yAxis: t1 }],
        [{ yAxis: t1, itemStyle: { color: OK_COLOR } }, { yAxis: t2 }],
        [{ yAxis: t2, itemStyle: { color: GOOD_COLOR } }, { yAxis: max }],
      )
    }
  }

  // Compute the cap before building the series so we can pass capped values.
  // Real values stay in `values` for the tooltip below.
  const dataMaxForSeries = values.length > 0 ? Math.max(...values) : 0
  const xMaxForSeries = target !== undefined
    ? Math.max(target * 2, target * 1.05)
    : dataMaxForSeries * 1.2
  const seriesValues = values.map((v) => Math.min(v, xMaxForSeries))

  const series: any[] = [
    {
      name: METRIC_LABELS[metric],
      type: 'bar',
      data: seriesValues,
      barMaxWidth: 32,
      ...(target !== undefined && {
        markLine: {
          symbol: 'none',
          silent: true,
          data: [
            {
              yAxis: target,
              lineStyle: { type: 'solid', width: 2 },
              label: {
                formatter: targetLabel ?? `Target: ${formatMetric(metric, target)}`,
                position: 'insideEndTop',
                fontSize: 10,
              },
            },
          ],
        },
      }),
      ...(markAreas.length > 0 && {
        markArea: { silent: true, data: markAreas },
      }),
    },
  ]

  // Axis cap (see series above) — pinned at 2× target so dramatic
  // over/under-target deltas don't drown out the target line + bands.
  const xMax = xMaxForSeries

  const option: any = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        const items = Array.isArray(params) ? params : [params]
        const header = items[0]?.axisValue || ''
        // Tooltip always shows the TRUE value, not the capped one — so the
        // chart visually pins to the right edge while the number stays honest.
        const idx = items[0]?.dataIndex ?? 0
        const trueValue = values[idx]
        const formatted = typeof trueValue === 'number' ? formatMetric(metric, trueValue) : '—'
        const targetTxt = target !== undefined ? `<br/>Target: <b>${formatMetric(metric, target)}</b>` : ''
        return `<b>${header}</b><br/>${METRIC_LABELS[metric]}: <b>${formatted}</b>${targetTxt}`
      },
    },
    grid: { left: '20%', right: 30, top: 15, bottom: 25, containLabel: false },
    xAxis: { type: 'value', max: xMax, axisLabel: { fontSize: 10 } },
    yAxis: {
      type: 'category',
      data: categories,
      axisLabel: { fontSize: 10, width: 120, overflow: 'truncate' },
      inverse: true,
    },
    series,
  }

  // Bullet uses a horizontal bar by convention so the target line reads as a
  // vertical reference. Re-orient the markLine accordingly.
  if (target !== undefined && series[0].markLine) {
    series[0].markLine.data[0].xAxis = target
    delete series[0].markLine.data[0].yAxis
  }
  if (markAreas.length > 0) {
    // Re-orient bands to xAxis (horizontal value axis).
    series[0].markArea.data = markAreas.map(([a, b]: any[]) => [
      { ...a, xAxis: a.yAxis, yAxis: undefined },
      { ...b, xAxis: b.yAxis, yAxis: undefined },
    ])
  }

  return (
    <ReactECharts
      option={option}
      theme={theme}
      style={{ height: '100%', width: '100%' }}
      opts={{ renderer: 'canvas' }}
    />
  )
}
